/* ==========================================================================
   FOOTBALL-THEME :: Cart drawer gesture + spring settle.
   Adds Apple-style physics on top of Dawn's cart drawer (assets/cart-drawer.js)
   without editing any core file:

   - Drag the open drawer to the RIGHT to dismiss: it follows the pointer 1:1,
     rubber-bands when pushed the wrong way, and the page dim follows the drag.
   - Release decides by momentum: a flick closes even from a short drag,
     otherwise the panel springs back open (velocity handed to the spring).
   - A running settle can be grabbed and reversed mid-flight - the panel is
     seized from its live (presentation) transform value.
   - Reduced motion: settles are instant.

   Delegated from document, so Dawn's section re-renders (cart item updates
   replace #CartDrawer's innerHTML) keep working. Loaded from
   layout/theme.liquid with the cart-drawer scripts.
   Depends on assets/football-physics.js.
   ========================================================================== */
(function () {
  'use strict';

  if (!window.fsPhysics) return;

  var reduceQuery =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function reduced() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  function scrimBase(el) {
    var color = window.getComputedStyle(el).backgroundColor || '';
    var match = color.match(/rgba?\(([^)]+)\)/);
    if (!match) return { rgb: '0, 0, 0', alpha: 0.5 };
    var parts = match[1].split(',').map(function (s) {
      return s.trim();
    });
    if (parts.length < 3) return { rgb: '0, 0, 0', alpha: 0.5 };
    return {
      rgb: parts.slice(0, 3).join(', '),
      alpha: parts.length > 3 ? parseFloat(parts[3]) : 1
    };
  }

  function swallowNextClick() {
    var handler = function (event) {
      cleanup();
      event.preventDefault();
      event.stopPropagation();
    };
    function cleanup() {
      window.clearTimeout(timer);
      document.removeEventListener('click', handler, true);
    }
    var timer = window.setTimeout(cleanup, 500);
    document.addEventListener('click', handler, true);
  }

  var gesture = null;
  var settle = null; /* { panel, spring } */

  document.addEventListener('pointerdown', function (event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    var panel = event.target.closest('.drawer__inner');
    if (!panel) return;
    var drawer = panel.closest('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return;
    if (event.target.closest('input, select, textarea, a, button, summary')) return;

    if (settle && settle.spring) settle.spring.stop();

    var scrim = scrimBase(drawer);
    gesture = {
      panel: panel,
      drawer: drawer,
      scrimRgb: scrim.rgb,
      scrimAlpha: scrim.alpha,
      startX: event.clientX,
      startY: event.clientY,
      base: window.fsPhysics.translate(panel, 'x'),
      width: panel.offsetWidth || 1,
      x: 0,
      engaged: false,
      tracker: window.fsPhysics.tracker()
    };
    gesture.tracker.add(event.clientX, performance.now());
  });

  window.addEventListener(
    'pointermove',
    function (event) {
      var g = gesture;
      if (!g) return;

      if (!g.engaged) {
        var mx = event.clientX - g.startX;
        var my = event.clientY - g.startY;
        if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
        if (Math.abs(my) >= Math.abs(mx)) {
          gesture = null; /* vertical: the cart list scrolls */
          return;
        }
        g.engaged = true;
        g.panel.classList.add('fs-cart-dragging');
        g.panel.style.transition = 'none';
        /* Seize from the live (presentation) value; restart from here. */
        g.base = window.fsPhysics.translate(g.panel, 'x');
        g.startX = event.clientX;
        g.startY = event.clientY;
        g.tracker.reset();
      }

      g.tracker.add(event.clientX, performance.now());

      var dx = event.clientX - g.startX;
      var x = g.base + dx;
      if (x < 0) {
        x = -window.fsPhysics.rubberband(-x, g.width);
      } else if (x > g.width) {
        x = g.width + window.fsPhysics.rubberband(x - g.width, g.width);
      }

      g.x = x;
      g.panel.style.transform = 'translateX(' + x.toFixed(2) + 'px)';
      var progress = window.fsPhysics.clamp(1 - x / g.width, 0, 1);
      g.drawer.style.backgroundColor =
        'rgba(' + g.scrimRgb + ', ' + (g.scrimAlpha * progress).toFixed(3) + ')';
    },
    { passive: true }
  );

  function endGesture() {
    var g = gesture;
    if (!g) return;
    gesture = null;
    if (!g.engaged) return;

    g.panel.classList.remove('fs-cart-dragging');
    var velocity = g.tracker.velocity(performance.now());
    var projected = g.x + window.fsPhysics.project(velocity);
    /* Velocity sign decides reverse vs commit; position only when parked. */
    var close;
    if (velocity > 250) {
      close = true; /* flicked toward dismiss */
    } else if (velocity < -250) {
      close = false; /* moving back toward open */
    } else {
      close = projected > g.width * 0.5; /* parked: judge by where it would rest */
    }

    swallowNextClick();

    var finish = function (closed) {
      /* Hand the transform back to Dawn's CSS at the off-screen position. */
      if (closed && typeof g.drawer.close === 'function') g.drawer.close();
      g.panel.style.transition = 'none';
      g.panel.style.transform = '';
      g.drawer.style.backgroundColor = '';
      void g.panel.offsetWidth;
      g.panel.style.transition = '';
    };

    if (reduced()) {
      finish(close);
      return;
    }

    if (!settle || settle.panel !== g.panel || !settle.spring) {
      settle = {
        panel: g.panel,
        spring: window.fsPhysics.spring({
          from: g.x,
          to: g.x,
          epsilon: 0.2,
          onUpdate: function (value) {
            g.panel.style.transform = 'translateX(' + value.toFixed(2) + 'px)';
            var progress = window.fsPhysics.clamp(1 - value / g.width, 0, 1);
            g.drawer.style.backgroundColor =
              'rgba(' + g.scrimRgb + ', ' + (g.scrimAlpha * progress).toFixed(3) + ')';
          }
        })
      };
    } else {
      settle.spring.jump(g.x);
    }

    settle.spring.retarget(
      close ? g.width : 0,
      velocity,
      close ? 0.9 : 1,
      close ? 0.35 : 0.45,
      function () {
        finish(close);
        if (settle && settle.panel === g.panel) settle.spring.stop();
      }
    );
  }

  window.addEventListener('pointerup', endGesture);
  window.addEventListener('pointercancel', endGesture);
})();
